import React from 'react';
import FieldSet from './components/FieldSets/FieldSet';
import Utils from '../../utils';
import PropTypes from 'prop-types';
import { errorStatePropTypes, schemaPropTypes } from './PropTypes/PropTypes';
import { phoneNumberPattern, emailPattern } from '../../constants/regex';
import moment from 'moment';

export default class ReactFormGenerator extends React.Component {
    constructor(props) {
        super(props);

        this.state = this.getStateFromProps(props);

        this.onFormFieldChange = this.onFormFieldChange.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.onBlur = this.onBlur.bind(this);
        this.onFocus = this.onFocus.bind(this);
    }

    getStateFromProps(props) {
        const { schema, errorState = {} } = props;
        const formData = this.getDefaultFormState(schema);

        return {
            formId: props.formId || this.generateFormId(),
            schema: this.getFormSchemaClone(schema),
            errorState,
            formData
        };
    }

    /**
     * Deep clones the schema object, done upon initialization or changing the schema
     * @param {Object} schema - schema
     * @returns {Object} schema
     */
    getFormSchemaClone(schema) {
        const fieldsClone = {};

        Object.keys(schema.fields).forEach(key => {
            if (Object.prototype.hasOwnProperty.call(schema.fields, key)) {
                fieldsClone[key] = {
                    ...schema.fields[key]
                };

                if (schema.fields[key].options) {
                    fieldsClone[key].options = { ...schema.fields[key].options };
                }

                if (schema.fields[key].uiSchema) {
                    fieldsClone[key].uiSchema = { ...schema.fields[key].uiSchema };

                    if (schema.fields[key].uiSchema.classes) {
                        fieldsClone[key].uiSchema.classes = { ...schema.fields[key].uiSchema.classes };
                    }
                }

                if (schema.fields[key].validator) {
                    fieldsClone[key].validator = { ...schema.fields[key].validator };

                    if (schema.fields[key].validator.length) {
                        fieldsClone[key].validator.length = { ...schema.fields[key].validator.length };
                    }

                    if (schema.fields[key].validator.value) {
                        fieldsClone[key].validator.value = { ...schema.fields[key].validator.value };

                        if (schema.fields[key].validator.value.oneOf) {
                            fieldsClone[key].validator.value.oneOf = [...schema.fields[key].validator.value.oneOf];
                        }
                    }

                    if (schema.fields[key].validator.date) {
                        fieldsClone[key].validator.date = { ...schema.fields[key].validator.date };
                    }

                    if (schema.fields[key].validator.labels) {
                        fieldsClone[key].validator.labels = { ...schema.fields[key].validator.labels };
                    }
                }
            }
        });

        return {
            ...schema,
            fields: fieldsClone
        };
    }

    getDefaultFormState(schema = { fields: {} }) {
        const { fields } = schema;
        const defaultFormFieldState = {};

        Object.keys(fields).forEach(key => {
            if (Object.prototype.hasOwnProperty.call(fields, key)) {
                if (Object.prototype.hasOwnProperty.call(fields[key], 'defaultValue')) {
                    defaultFormFieldState[key] = fields[key].defaultValue;
                } else {
                    if (fields[key].multiple) {
                        defaultFormFieldState[key] = [];
                    } else {
                        defaultFormFieldState[key] = null;
                    }
                }
            }
        });

        // propagate the form data
        this.propagateFieldChange({ schema, formData: defaultFormFieldState }, null, null);

        return defaultFormFieldState;
    }

    getUpdatedFormState(prevProps) {
        const propsSchema = this.props.schema;
        const inputFormData = this.state.formData;
        const propsFormErrors = this.props.errorState;
        const inputFormErrors = this.state.errorState;

        let updateState = false;
        const currentSchema = this.state.schema;
        const newState = {
            schema: { ...currentSchema },
            errorState: { ...inputFormErrors }
        };

        if (!Utils.isEqual(currentSchema, propsSchema)) {
            newState.schema = this.getFormSchemaClone(propsSchema);
            updateState = true;
        }

        // check if there are errors given through props that are not in the errorState
        if (propsFormErrors) {
            Object.keys(propsFormErrors).forEach(key => {
                if (Object.prototype.hasOwnProperty.call(propsFormErrors, key) &&
                    propsFormErrors[key] === null && newState.errorState[key]) {
                    updateState = true;
                    newState.errorState[key] = null;
                    delete newState.errorState[key];
                }

                if (Object.prototype.hasOwnProperty.call(propsFormErrors, key) &&
                    propsFormErrors[key] && propsFormErrors[key].errorText) {

                    if (!inputFormErrors[key] || inputFormErrors[key].errorText !== propsFormErrors[key].errorText) {
                        updateState = true;
                        newState.errorState[key] = {
                            errorText: propsFormErrors[key].errorText
                        };
                    }
                }
            });
        }

        const removedFields = [];

        // rebuild the new form data values according to the schema fields
        // remove field from form data if it was removed from the schema in an update
        Object.keys(inputFormData).forEach(key => {
            if (Object.prototype.hasOwnProperty.call(inputFormData, key) &&
                Object.prototype.hasOwnProperty.call(propsSchema.fields, key)) {

                if (!newState.formData) {
                    newState.formData = {};
                }

                newState.formData[key] = inputFormData[key];

                // no need to update state, because field removal is a schema change
                // which is already setting updateState in that comparison above
            } else {
                removedFields.push(key);
            }
        });

        // propagate field change for all removed fields
        removedFields.forEach(key => {
            this.propagateFieldChange(newState, key, null);
        });

        // override default value in the stored form data
        Object.keys(propsSchema.fields).forEach(key => {
            if (Object.prototype.hasOwnProperty.call(propsSchema.fields, key)) {
                if (prevProps.schema.fields) {
                    let prevField = {};

                    if (Object.prototype.hasOwnProperty.call(prevProps.schema.fields, key)) {
                        prevField = prevProps.schema.fields[key];
                    }

                    const currField = propsSchema.fields[key];

                    // check if the field has been disabled
                    if (!prevField.disabled && currField.disabled) {

                        // create new form state if not already done so
                        if (!newState.formData) {
                            newState.formData = {
                                ...inputFormData
                            };
                        }

                        // propagate field change for disabled change
                        this.propagateFieldChange(newState, key, null);
                    }

                    // check if the value options have been changed, verify the selected value
                    // this happens before checking default value changes, otherwise defaultValue will be overridden
                    if (!Utils.isEqual(prevField.valueOptions, currField.valueOptions)) {

                        // check a form value was set, and verify if the option is still valid
                        if (inputFormData[key]) {

                            // create new form state if not already done so
                            if (!newState.formData) {
                                newState.formData = {
                                    ...inputFormData
                                };
                            }

                            // on multiple fields, filter result, or reset if not multiple
                            if (currField.multiple) {
                                const validFieldValues = [];

                                // todo: we could enable this in the future to filter the selected values, but for now always reset
                                inputFormData[key].forEach(selectedValue => {
                                    for (let i = 0; i < currField.valueOptions.length; i++) {
                                        if (
                                            currField.valueOptions[i].key === selectedValue &&
                                            !currField.valueOptions[i].deselect
                                        ) {
                                            validFieldValues.push(selectedValue);
                                            break;
                                        }
                                    }
                                });

                                newState.formData[key] = validFieldValues;
                            } else {
                                newState.formData[key] = null;
                            }

                            // propagate field change, upon removal of invalid options
                            this.propagateFieldChange(newState, key, newState.formData[key]);
                        }
                    }

                    // detect changes in default value
                    if (prevField.defaultValue !== currField.defaultValue) {
                        if (!newState.formData) {
                            newState.formData = {
                                ...inputFormData
                            };
                        }

                        newState.formData[key] = currField.defaultValue;

                        // propagate field change, upon changing the default value (override)
                        this.propagateFieldChange(newState, key, newState.formData[key]);

                        updateState = true;
                    }
                }
            }
        });

        if (updateState) {
            this.setState(newState);
        }
    }

    getAvailableFormData(schema, formData) {
        const availableFormData = {};

        // remove disabled fields
        Object.keys(formData).forEach(key => {
            if (!schema.fields[key].disabled) {
                availableFormData[key] = formData[key];
            }
        });

        return availableFormData;
    }

    propagateFieldChange(formState = { schema: {}, formData: {}, errorState: null }, fieldId, fieldValue) {
        const formData = this.getAvailableFormData(formState.schema, formState.formData);

        if (this.props.onChange) {
            this.props.onChange(
                {
                    errorState: formState.errorState,
                    formData
                },
                {
                    fieldId,
                    fieldValue
                },
                {
                    formId: this.props.formId
                }
            );
        }
    }

    generateFormId() {
        return `form_${Math.random().toString(36).slice(7)}`;
    }

    componentDidMount() {
        if (!this.state.formId) {
            this.setState({
                formId: this.generateFormId()
            });
        }
    }

    componentDidUpdate(prevProps) {
        this.getUpdatedFormState(prevProps);
    }

    genericValidateFormField(fieldId, fieldValue) {
        const validator = this.state.schema.fields[fieldId].validator;

        // don't validate without a validator or when the field is disabled
        if (!validator || this.state.schema.fields[fieldId].disabled) {
            return null;
        }

        if (validator.required && !fieldValue && fieldValue !== 0) {
            return validator.labels.required;
        }

        if (validator.required && Utils.isArray(fieldValue) && fieldValue.length === 0) {
            return validator.labels.required;
        }

        if (validator.length) {
            if (fieldValue.length < validator.length.min) {
                return validator.labels.lengthMin;
            }

            if (fieldValue.length > validator.length.max) {
                return validator.labels.lengthMax;
            }
        }

        if (validator.value) {
            if (fieldValue < validator.value.min) {
                return validator.labels.valueMin;
            }

            if (fieldValue > validator.value.max) {
                return validator.labels.valueMax;
            }

            if (validator.value.oneOf && !~validator.value.oneOf.indexOf(fieldValue)) {
                return validator.labels.oneOf;
            }
        }

        if (validator.date && fieldValue instanceof moment) {
            if (fieldValue.isBefore(validator.date.min)) {
                return validator.labels.dateMin;
            }

            if (fieldValue.isAfter(validator.date.max)) {
                return validator.labels.dateMax;
            }
        }

        // Phone validation
        if (validator.invalidPhone && !phoneNumberPattern.test(fieldValue)) {
            return validator.invalidPhone;
        }

        // Email validation
        if (validator.isEmail && fieldValue) {
            if (fieldValue.includes(' ')) {
                return validator.labels.emailContainsSpace;
            }

            if (!emailPattern.test(fieldValue)) {
                return validator.labels.emailInvalid;
            }
        }

        return null;
    }

    validateFormField(fieldId, fieldValue) {
        let formErrors = this.genericValidateFormField(fieldId, fieldValue);

        if (this.props.validator) {
            formErrors = this.props.validator({
                changedField: {
                    fieldId,
                    fieldValue
                },
                formData: {
                    ...this.state.formData,
                    [fieldId]: fieldValue
                }
            }) || formErrors;
        }

        return formErrors;
    }

    onFormFieldChange(fieldId, fieldValue) {
        const { noValidate, liveValidate } = this.props;
        const { schema, formData, errorState } = this.state;

        const newFormData = {
            ...formData,
            [fieldId]: fieldValue
        };

        const mustValidate = !noValidate && liveValidate;
        const newState = {
            schema: { ...schema },
            formData: newFormData,
            errorState
        };

        let formFieldError = true;

        if (mustValidate) {
            formFieldError = this.validateFormField(fieldId, newFormData[fieldId]);

            if (formFieldError !== null) {
                newState.errorState = {
                    ...newState.errorState,
                    [fieldId]: {
                        errorText: formFieldError
                    }
                };
            } else {
                newState.errorState[fieldId] = null;
                delete newState.errorState[fieldId];
            }
        }

        this.setState(
            newState,
            () => this.propagateFieldChange(newState, fieldId, fieldValue)
        );
    }

    validateFormFields(fields) {
        const { errorState } = this.state;
        const newErrorState = {
            ...errorState
        };

        Object.keys(fields).forEach(key => {
            if (Object.prototype.hasOwnProperty.call(fields, key)) {
                const formFieldError = this.validateFormField(key, fields[key]);

                if (formFieldError !== null) {
                    newErrorState[key] = {
                        errorText: formFieldError
                    };
                } else {
                    newErrorState[key] = null;
                    delete newErrorState[key];
                }
            }
        });

        return newErrorState;
    }

    hasFormErrors(errorState, formData) {
        let hasError = false;

        Object.keys(formData).forEach(key => {
            if (Object.prototype.hasOwnProperty.call(formData, key)) {
                if (errorState[key]) {
                    hasError = true;
                }
            }
        });

        return hasError;
    }

    onSubmit(event) {
        event.preventDefault();

        const formData = this.getAvailableFormData(this.state.schema, this.state.formData);

        const newErrorState = this.validateFormFields(formData);

        this.setState(
            {
                errorState: newErrorState === null ? {} : newErrorState
            },
            () => {
                if (!this.hasFormErrors(newErrorState, formData) && this.props.onSubmit) {
                    this.props.onSubmit(formData, newErrorState);
                } else if (this.props.onChange) {
                    this.props.onChange(
                        {
                            errorState: newErrorState,
                            formData
                        },
                        null,
                        {
                            formId: this.props.formId
                        }
                    );
                }
            }
        );
    }

    onBlur(field) {
        if (this.props.onBlurValidate) {
            const formFieldError = this.validateFormField(field.id, this.state.formData[field.id]);
            const newErrorState = {
                ...this.state.errorState
            };

            if (formFieldError !== null) {
                newErrorState[field.id] = {
                    errorText: formFieldError
                };
            } else {
                newErrorState[field.id] = null;
                delete newErrorState[field.id];
            }

            this.setState({
                errorState: newErrorState
            });
        }

        if (this.props.onBlur) {
            this.props.onBlur(field);
        }
    }

    onFocus(...args) {
        if (this.props.onFocus) {
            this.props.onFocus(...args);
        }
    }

    render() {
        const {
            formTag,
            fieldSetComponent,
            children,
            hideSubmit,
            submitButton,
            fieldClasses,
            fieldSetClasses,
            groupClasses,
            classes,
            autoComplete = 'off',
            submitOnEnter
        } = this.props;

        const {
            formId,
            schema,
            formData,
            errorState
        } = this.state;

        const FormTag = formTag ? formTag : 'form';
        const FormFieldSet = fieldSetComponent ? fieldSetComponent : FieldSet;

        return (
            <FormTag
                id={ formId }
                className={ classes.root }
                autoComplete={ autoComplete }
                onSubmit={ this.onSubmit }
                onKeyDown={ event => {
                    if (!submitOnEnter && event.keyCode === 13) { /* 13 is global keycode for Enter */
                        event.preventDefault();
                    }
                }}
            >
                <FormFieldSet
                    schema={ schema }
                    formData={ formData }
                    onChange={ this.onFormFieldChange }
                    onBlur={ this.onBlur }
                    onFocus={ this.onFocus }
                    fieldClasses={ fieldClasses }
                    fieldSetClasses={ fieldSetClasses }
                    groupClasses={ groupClasses }
                    errorState={ errorState }
                />
                { children || hideSubmit ? (
                    children
                ) : (
                    <div
                        className={ submitButton.classes.buttonContainer }
                    >
                        <button
                            data-cy={`submit-${formId}-button`}
                            type="submit"
                            className={ submitButton.classes.root }
                        >
                            { submitButton.label || 'Submit' }
                        </button>
                    </div>
                )}
            </FormTag>
        );
    }
}

ReactFormGenerator.propTypes = {

    /**
     * Documentation is listed in the schemaPropTypes
     */
    schema: schemaPropTypes.isRequired,

    /**
     * Form HTML tag, defaulting to <form>
     * @example
     * form
     */
    formTag: PropTypes.string,

    /**
     * Overriding for the field set. This will use the default FieldSet initially.
     * This should be a class/component reference
     * @example
     * CustomFieldSet
     */
    fieldSetComponent: PropTypes.elementType,

    /**
     * Provide label and classes for the default submit button
     * This will only apply if no children were passed to the form
     * @example
     * {
     *     label: 'submit',
     *     classes: {
     *         root: 'rootClass',
     *         buttonContainer: 'buttonContainerClass'
     *     }
     * }
     */
    submitButton: PropTypes.shape({
        label: PropTypes.string,
        classes: PropTypes.shape({
            root: PropTypes.string,
            buttonContainer: PropTypes.string
        })
    }),

    /**
     * Classes that apply to all field containers rendered by the FieldSet
     * @example
     * {
     *     root: 'defaultFieldClass'
     * }
     */
    fieldClasses: PropTypes.shape({
        root: PropTypes.string
    }),

    /**
     * Classes that apply to all field containers rendered by the FieldSet
     * @example
     * {
     *     root: 'defaultFieldClass'
     * }
     */
    fieldSetClasses: PropTypes.shape({
        root: PropTypes.string
    }),

    /**
     * Classes that apply to the form component
     * @example
     * {
     *     root: 'formRootClass'
     * }
     */
    classes: PropTypes.shape({
        root: PropTypes.string
    }),

    /**
     * Autocomplete of the form. Default set to off
     * @example
     * 'on'
     */
    autoComplete: PropTypes.string,

    /**
     * Default error states for the form. When validation happens, this will override the default error state,
     * @example see errorStatePropTypes
     */
    errorState: errorStatePropTypes,

    /**
     * form id - unique id is generated if not given
     * @example
     * 'form_id'
     */
    formId: PropTypes.string,

    /**
     * onFocus handler for all fields
     * @example
     * field => {
     *     // field.id
     *     // field.value
     * }
     */
    onFocus: PropTypes.func,

    /**
     * onBlur handler for all fields
     * @example
     * field => {
     *     // field.id
     *     // field.value
     * }
     */
    onBlur: PropTypes.func,

    /**
     * Validate form fields on blur event
     * @example
     * true
     */
    onBlurValidate: PropTypes.bool,

    /**
     * onChange handler for all form fields
     * When the form is submitted, but validation returned errors, an onChange is called with changedField unset.
     * @example
     * (state, changedField) => {
     *     // state.formData
     *     // state.errorState
     *     // changedField.fieldId
     *     // changedField.fieldValue
     *     // changedField is unset when submitting the form with form errors
     * }
     */
    onChange: PropTypes.func,

    /**
     * onSubmit handler, will only get called when submitting without any form errors
     * @example
     * formData => {
     *     // const fieldKeyValue = formData[fieldKey]
     * }
     */
    onSubmit: PropTypes.func,

    /**
     * Custom validator method that will extend the default validation rules, provided in the validator field object
     * @example
     * ({ changedField, formData}) => {
     *      // changedField.fieldValue
     *      // changedField.fieldValue
     *      // const fieldKeyValue = formData[fieldKey]
     * }
     */
    validator: PropTypes.func,

    /**
     * Switch off validation completely
     * @example
     * false
     */
    noValidate: PropTypes.bool,

    /**
     * Flag to live validate the field that is being changed
     * @example
     * true
     */
    liveValidate: PropTypes.bool,

    /**
     * Hide the default submit button
     * @example
     * true
     */
    hideSubmit: PropTypes.bool,

    /**
     * Capture onKeyDown on the form to do or do not submit when pressing enter in a form field
     * @example
     * true
     */
    submitOnEnter: PropTypes.bool
};

ReactFormGenerator.defaultProps = {
    submitButton: {
        label: '',
        classes: {
            root: '',
            buttonContainer: ''
        }
    },
    fieldClasses: {
        root: ''
    },
    fieldSetClasses: {
        root: ''
    },
    groupClasses: {},
    classes: {
        root: ''
    },
    autoComplete: 'off',
    noValidate: false,
    liveValidate: false,
    hideSubmit: false,
    submitOnEnter: false
};
